import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { 
  User, 
  Mail, 
  Phone, 
  Building, 
  MapPin, 
  Calendar,
  Edit3,
  Save,
  X,
  Camera,
  Check,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  bio?: string;
  location?: string;
  avatar?: string;
  createdAt: string;
  updatedAt: string;
}

const Profile: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [removeAvatarRequested, setRemoveAvatarRequested] = useState<boolean>(false);

  const resolveAvatarSrc = (avatar: string, updatedAt: string) => {
    if (!avatar) return '';
    // Support API-served path (/api/users/profile/photo/file/...) and absolute URLs
    const isApiPath = avatar.startsWith('/api/');
    const isAbsolute = /^https?:\/\//i.test(avatar);
    const isBareFilename = !avatar.startsWith('/') && !isAbsolute;
    const base = (process.env.REACT_APP_API_BASE_URL as string) || '';
    const cacheBuster = updatedAt ? `?t=${encodeURIComponent(updatedAt)}` : '';
    if (isAbsolute) return `${avatar}${cacheBuster}`;
    if (isBareFilename) return `${base}/api/users/profile/photo/file/${avatar}${cacheBuster}`;
    if (isApiPath) return `${base}${avatar}${cacheBuster}`;
    // Fallback for /uploads if present
    const isUploadsRelative = avatar.startsWith('/uploads');
    return `${isUploadsRelative ? base : ''}${avatar}${cacheBuster}`;
  };
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    bio: '',
    location: ''
  });

  useEffect(() => {
    if (user) {
      // Handle date conversion safely
      const formatDate = (date: any): string => {
        if (!date) return new Date().toISOString();
        if (typeof date === 'string') return date;
        if (date instanceof Date) return date.toISOString();
        // If it's a timestamp or other format, try to convert
        return new Date(date).toISOString();
      };

      const userProfile: UserProfile = {
        id: user._id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone || '',
        company: user.company || '',
        bio: '',
        location: '',
        avatar: user.avatar || '',
        createdAt: formatDate(user.createdAt),
        updatedAt: formatDate(user.updatedAt)
      };
      
      setProfile(userProfile);
      setFormData({
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        email: userProfile.email,
        phone: userProfile.phone || '',
        company: userProfile.company || '',
        bio: userProfile.bio || '',
        location: userProfile.location || ''
      });
      setLoading(false);
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!profile) return;
    
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // If a photo is selected or avatar removal requested, send multipart FormData
      if (selectedPhoto || removeAvatarRequested) {
        const multipart = new FormData();
        multipart.append('firstName', formData.firstName);
        multipart.append('lastName', formData.lastName);
        multipart.append('email', formData.email);
        multipart.append('phone', formData.phone);
        multipart.append('company', formData.company);
        multipart.append('bio', formData.bio);
        multipart.append('location', formData.location);
        if (selectedPhoto) {
          multipart.append('photo', selectedPhoto);
        } else if (removeAvatarRequested) {
          // Explicitly clear avatar
          multipart.append('avatar', '');
        }

        const result = await updateProfile(multipart as any);
        if (!result.success) {
          setError(result.error || 'Failed to update profile. Please try again.');
          setSaving(false);
          return;
        }
      } else {
        // JSON-only update
        const result = await updateProfile(formData);
        if (!result.success) {
          setError(result.error || 'Failed to update profile. Please try again.');
          setSaving(false);
          return;
        }
      }
      
      // Update local profile state using server response from context if present
      // After updateProfile, AuthContext.user is updated; use it for consistency
      const stored = localStorage.getItem('user');
      let newAvatar = profile.avatar;
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.avatar !== undefined) newAvatar = parsed.avatar;
        } catch {}
      }

      setProfile(prev => prev ? { 
        ...prev, 
        ...formData, 
        avatar: newAvatar || '',
        updatedAt: new Date().toISOString() 
      } : null);

      // Reset file/removal state
      setSelectedPhoto(null);
      setRemoveAvatarRequested(false);

      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedPhoto(file);
      setRemoveAvatarRequested(false);
      // For now, we'll just show a preview. In production, you'd upload to server
      const reader = new FileReader();
      reader.onload = (event) => {
        const avatarUrl = event.target?.result as string;
        setProfile(prev => prev ? { ...prev, avatar: avatarUrl } : null);
        // You can also update formData if needed
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAvatar = () => {
    setProfile(prev => prev ? { ...prev, avatar: '' } : null);
    setSelectedPhoto(null);
    setRemoveAvatarRequested(true);
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        phone: profile.phone || '',
        company: profile.company || '',
        bio: profile.bio || '',
        location: profile.location || ''
      });
    }
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Debug logging
  console.log('Profile component render:', { user, profile, loading });

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500 mx-auto mb-8"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading Profile...</h2>
          <p className="text-gray-600">Fetching your profile information</p>
          <p className="text-sm text-gray-500 mt-4">Debug: User = {user ? 'Present' : 'Not found'}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="text-red-500 text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600 mb-8">Please log in to view your profile.</p>
          <button 
            onClick={() => window.location.href = '/login'} 
            className="btn-primary"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Profile Not Found</h2>
          <p className="text-gray-600 mb-8">Unable to load your profile information.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16">
      <SEOHead 
        title="Profile - Manage Your CRMONCE Account"
        description="Manage your CRMONCE profile, update personal information, and customize your account settings for Microsoft Dynamics 365 and Power Platform services."
        keywords="CRMONCE profile, account settings, user profile, personal information, account management"
        canonical="/profile"
        noIndex={true}
      />
      
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            <span className="gradient-text">My Profile</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Manage your account information and preferences
          </p>
        </motion.div>

        {/* Success/Error Messages */}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center"
          >
            <Check className="w-5 h-5 mr-2" />
            {success}
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center"
          >
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </motion.div>
        )}

        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Profile Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 sticky top-8">
                                 {/* Avatar */}
                 <div className="text-center mb-6">
                   <div className="relative inline-block">
                     <div className="w-32 h-32 rounded-full bg-primary-100 flex items-center justify-center mx-auto border-4 border-primary-200 overflow-hidden">
                      {profile?.avatar ? (
                         <img 
                          src={resolveAvatarSrc(profile.avatar, profile.updatedAt)}
                           alt="Profile" 
                           className="w-full h-full object-cover"
                           onError={(e) => {
                             const target = e.target as HTMLImageElement;
                             target.style.display = 'none';
                             const nextSibling = target.nextSibling as HTMLElement;
                             if (nextSibling) nextSibling.style.display = 'flex';
                           }}
                         />
                       ) : null}
                       <User className="w-16 h-16 text-primary-600" style={{ display: profile?.avatar ? 'none' : 'flex' }} />
                     </div>
                     <button 
                       onClick={() => document.getElementById('avatar-upload')?.click()}
                       className="absolute bottom-2 right-2 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center hover:bg-primary-700 transition-colors"
                     >
                       <Camera className="w-4 h-4" />
                     </button>
                     <input
                       id="avatar-upload"
                       type="file"
                       accept="image/*"
                       className="hidden"
                       onChange={handleAvatarChange}
                     />
                   </div>
                   <h2 className="text-2xl font-bold text-gray-900 mt-4">
                     {profile.firstName} {profile.lastName}
                   </h2>
                   <p className="text-gray-600">{profile.email}</p>
                   {profile?.avatar && (
                     <button
                       onClick={handleRemoveAvatar}
                       className="mt-2 px-3 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded border border-red-200"
                     >
                       Remove Photo
                     </button>
                   )}
                 </div>

                {/* Quick Stats */}
                <div className="space-y-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-3" />
                    <span>Joined {formatDate(profile.createdAt)}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Mail className="w-4 h-4 mr-3" />
                    <span>{profile.email}</span>
                  </div>
                  {profile.phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone className="w-4 h-4 mr-3" />
                      <span>{profile.phone}</span>
                    </div>
                  )}
                  {profile.company && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Building className="w-4 h-4 mr-3" />
                      <span>{profile.company}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="mt-8 space-y-3">
                  {!isEditing ? (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setIsEditing(true)}
                      className="w-full btn-primary flex items-center justify-center"
                    >
                      <Edit3 className="w-4 h-4 mr-2" />
                      Edit Profile
                    </motion.button>
                  ) : (
                    <div className="space-y-3">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleSave}
                        disabled={saving}
                        className="w-full btn-primary flex items-center justify-center disabled:opacity-50"
                      >
                        {saving ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        {saving ? 'Saving...' : 'Save Changes'}
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleCancel}
                        className="w-full btn-outline flex items-center justify-center"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </motion.button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Profile Details */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="lg:col-span-2"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Profile Information</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter your first name"
                      />
                    ) : (
                      <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <User className="w-5 h-5 text-gray-400 mr-3" />
                        <span className="text-gray-900">{profile.firstName}</span>
                      </div>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter your last name"
                      />
                    ) : (
                      <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <User className="w-5 h-5 text-gray-400 mr-3" />
                        <span className="text-gray-900">{profile.lastName}</span>
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    {isEditing ? (
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter your email"
                      />
                    ) : (
                      <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <Mail className="w-5 h-5 text-gray-400 mr-3" />
                        <span className="text-gray-900">{profile.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    {isEditing ? (
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter your phone number"
                      />
                    ) : (
                      <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <Phone className="w-5 h-5 text-gray-400 mr-3" />
                        <span className="text-gray-900">{profile.phone || 'Not provided'}</span>
                      </div>
                    )}
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter your company name"
                      />
                    ) : (
                      <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <Building className="w-5 h-5 text-gray-400 mr-3" />
                        <span className="text-gray-900">{profile.company || 'Not provided'}</span>
                      </div>
                    )}
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter your location"
                      />
                    ) : (
                      <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                        <span className="text-gray-900">{profile.location || 'Not provided'}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bio */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bio
                  </label>
                  {isEditing ? (
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Tell us about yourself..."
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-900">
                        {profile.bio || 'No bio provided'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
